/*
 * Practicum section logos — imported from Figma.
 *
 * Each logo is "prActicum <section>" with a colored star in the A.
 * Star color identifies the section.
 *
 * help  → teal star   #069B93
 * school→ blue star   #1192E6
 * media → orange star #FF5E0E
 * group → green star  #57B745 (preloader only, not exported)
 */

import helpPaths from "@/shared/ui/icons/svg-o97u2f18ak";
import schoolPaths from "@/shared/ui/icons/svg-xgh9f7apn2";
import mediaPaths from "@/shared/ui/icons/svg-g9h100ep06";
import groupPaths from "@/shared/ui/icons/svg-jbkew87gw3";

import type { PanelId } from "@/shared/types";

interface LogoProps {
  /** Height in px — width auto-scales from viewBox aspect ratio */
  height?: number;
  className?: string;
  style?: React.CSSProperties;
  /** Override the star fill (e.g. '#fff' on colored backgrounds) */
  starColor?: string;
}

/* ── HELP ── */
function PracticumHelpLogo({ className, starColor, style }: LogoProps) {
  return (
    <svg className={className} style={style} viewBox="0 0 282.876 60.358" fill="none">
      <path d={helpPaths.p6b17300} fill="currentColor" />
      <path d={helpPaths.p2f9b8100} fill="currentColor" />
      <path d={helpPaths.p16f88f00} fill="currentColor" />
      <path d={helpPaths.pd621100} fill="currentColor" />
      <path d={helpPaths.pb961400} fill="currentColor" />
      <path d={helpPaths.p25fb9700} fill="currentColor" />
      <path d={helpPaths.p14689480} fill="currentColor" />
      <path d={helpPaths.p285c6180} fill="currentColor" />
      <path
        clipRule="evenodd"
        d={helpPaths.p2e4eba00}
        fill="currentColor"
        fillRule="evenodd"
      />
      <path
        clipRule="evenodd"
        d={helpPaths.p1d5a8d00}
        fill={starColor || "#069B93"}
        fillRule="evenodd"
      />
      <path d={helpPaths.p3b8c4700} fill="currentColor" />
      <path d={helpPaths.p320af300} fill="currentColor" />
      <path d={helpPaths.p282e7b00} fill="currentColor" />
      <path d={helpPaths.p36671240} fill="currentColor" />
    </svg>
  );
}

/* ── SCHOOL ── */
function PracticumSchoolLogo({ className, starColor, style }: LogoProps) {
  return (
    <svg className={className} style={style} viewBox="0 0 282.876 60.366" fill="none">
      <path d={schoolPaths.p17f050c0} fill="currentColor" />
      <path d={schoolPaths.p1919af00} fill="currentColor" />
      <path d={schoolPaths.p20466f00} fill="currentColor" />
      <path d={schoolPaths.p1ad6b600} fill="currentColor" />
      <path d={schoolPaths.p241d8f80} fill="currentColor" />
      <path d={schoolPaths.p2af9e00} fill="currentColor" />
      <path d={schoolPaths.p3f856870} fill="currentColor" />
      <path d={schoolPaths.pfa63000} fill="currentColor" />
      <path
        clipRule="evenodd"
        d={schoolPaths.p993c880}
        fill="currentColor"
        fillRule="evenodd"
      />
      <path
        clipRule="evenodd"
        d={schoolPaths.p8275a00}
        fill={starColor || "#1192E6"}
        fillRule="evenodd"
      />
      <path d={schoolPaths.p38463200} fill="currentColor" />
      <path d={schoolPaths.p2d15ba00} fill="currentColor" />
      <path d={schoolPaths.p1fbc4900} fill="currentColor" />
      <path d={schoolPaths.p1c653f0} fill="currentColor" />
      <path d={schoolPaths.p2c3c4080} fill="currentColor" />
      <path d={schoolPaths.p622c600} fill="currentColor" />
    </svg>
  );
}

/* ── MEDIA ── */
function PracticumMediaLogo({ className, starColor, style }: LogoProps) {
  return (
    <svg className={className} style={style} viewBox="0 0 282.876 60.548" fill="none">
      <path d={mediaPaths.p37d65f00} fill="currentColor" />
      <path d={mediaPaths.p26f2d300} fill="currentColor" />
      <path d={mediaPaths.p2dbc6630} fill="currentColor" />
      <path d={mediaPaths.pc698300} fill="currentColor" />
      <path d={mediaPaths.p3c72e200} fill="currentColor" />
      <path d={mediaPaths.p38a44d00} fill="currentColor" />
      <path d={mediaPaths.p16b3f700} fill="currentColor" />
      <path d={mediaPaths.pa113880} fill="currentColor" />
      <path
        clipRule="evenodd"
        d={mediaPaths.p17fda300}
        fill="currentColor"
        fillRule="evenodd"
      />
      <path
        clipRule="evenodd"
        d={mediaPaths.p18c3f580}
        fill={starColor || "#FF5E0E"}
        fillRule="evenodd"
      />
      <path d={mediaPaths.p2c305600} fill="currentColor" />
      <path d={mediaPaths.p197a2cc0} fill="currentColor" />
      <path d={mediaPaths.p3a45d80} fill="currentColor" />
      <path d={mediaPaths.p1dda4900} fill="currentColor" />
      <path d={mediaPaths.pe408680} fill="currentColor" />
    </svg>
  );
}

/* ── Convenience map ── */

const LOGO_MAP: Record<PanelId, React.FC<LogoProps>> = {
  help: PracticumHelpLogo,
  school: PracticumSchoolLogo,
  media: PracticumMediaLogo,
};

export function PracticumLogo({ id, ...props }: LogoProps & { id: PanelId }) {
  const Logo = LOGO_MAP[id];
  return <Logo {...props} />;
}

/* ── GROUP (preloader logo) ── */
export function PracticumGroupLogo({
  className,
  starColor,
  height,
  style,
}: LogoProps & { width?: number }) {
  const svgWidth = height != null ? Math.round((height * 282.876) / 58.2) : undefined;
  return (
    <svg
      className={className}
      viewBox="0 0 282.876 58.2"
      fill="none"
      width={svgWidth}
      height={height}
      style={style}
    >
      <path d={groupPaths.p3fc7a600} fill="currentColor" />
      <path
        clipRule="evenodd"
        d={groupPaths.pe03e000}
        fill="currentColor"
        fillRule="evenodd"
      />
      <path
        clipRule="evenodd"
        d={groupPaths.p2256cd00}
        fill="currentColor"
        fillRule="evenodd"
      />
      <path
        clipRule="evenodd"
        d={groupPaths.p5a62180}
        fill={starColor || "#57B745"}
        fillRule="evenodd"
      />
      <path d={groupPaths.p26983200} fill="currentColor" />
      <path d={groupPaths.p8c6f080} fill="currentColor" />
      <path d={groupPaths.p12952c00} fill="currentColor" />
      <path d={groupPaths.p3b2f5000} fill="currentColor" />
      <path d={groupPaths.p92d1d00} fill="currentColor" />
      <path d={groupPaths.p1b7efe00} fill="currentColor" />
    </svg>
  );
}

/* ── GROUP logo mark — letter A + star only (isolated from full logo) ── */
export function PracticumGroupLogoMark({
  height = 24,
  starColor,
  color,
}: {
  height?: number;
  starColor?: string;
  color?: string;
}) {
  // viewBox isolates just the A+star region of the 282.876×58.2 logo
  // x: 59–110, y: 2–58 → width≈51, height≈56
  const vbW = 51;
  const vbH = 56;
  const w = Math.round((height * vbW) / vbH);
  const A_PATH =
    "M63.914 31.86C68.02 22.91 74.562 8.70998 75.471 7.10002C76.844 4.65998 78.553 3.74006 81.28 3.74006C83.725 3.74006 85.713 4.54006 86.915 7.10002C87.985 9.37004 100.439 37.92 100.439 37.92L109.085 57.62H94.864L87.614 40.8401C87.646 40.93 87.678 41.01 87.708 41.1L87.368 40.31C88.144 40.26 88.962 40.2401 89.822 40.25V39.93C88.885 39.93 87.998 39.9 87.16 39.83L86.657 38.66H86.672L75.141 11.97L65.484 31.86H63.914Z";
  const STAR_PATH =
    "M87.16 39.83C78.871 39.1401 75.364 35.02 75.364 26.4299H75.045C75.045 35.89 70.899 39.9299 60.693 39.9299V40.25C70.899 40.14 75.045 44.0799 75.045 53.65H75.364C75.364 44.8799 78.93 40.85 87.368 40.31L87.16 39.83Z";
  return (
    <svg
      width={w}
      height={height}
      viewBox={`59 2 ${vbW} ${vbH}`}
      fill="none"
      style={{ display: "block" }}
    >
      <path
        d={A_PATH}
        fillRule="evenodd"
        clipRule="evenodd"
        fill={color || "currentColor"}
      />
      <path
        d={STAR_PATH}
        fillRule="evenodd"
        clipRule="evenodd"
        fill={starColor || "#57B745"}
      />
    </svg>
  );
}

/* ── GROUP star icon (small standalone) ── */
const STAR_ICON_PATH =
  "M14.671 27.2119C14.671 17.6439 18.923 13.7109 29.129 13.817V13.4979C18.923 13.4979 14.671 9.45801 14.671 0H14.352C14.352 9.45801 10.206 13.4979 0 13.4979V13.817C10.206 13.7109 14.352 17.6439 14.352 27.2119H14.671Z";

export function PracticumGroupLogoStar({
  height = 20,
  starColor,
}: {
  height?: number;
  starColor?: string;
}) {
  const w = (height * 29.129) / 27.2119;
  return (
    <svg
      width={w}
      height={height}
      viewBox="0 0 29.129 27.2119"
      fill="none"
      style={{ display: "block" }}
    >
      <path
        d={STAR_ICON_PATH}
        fillRule="evenodd"
        clipRule="evenodd"
        fill={starColor || "#57B745"}
      />
    </svg>
  );
}
